import ReactLib = require("react");

export const COLOR_KEYS = ["primary", "light", "dark"] as const;
export const IMAGE_KEYS = [
    "photo01",
    "photo02",
    "photo03",
    "photo04",
    "photo05",
    "photo06",
    "photo07",
    "photo08",
    "photo09",
    "photo10",
    "logoHorizontal",
    "logoHorizontalLight",
    "logoHorizontalDark",
    "logoStacked",
    "logoStackedLight",
    "logoStackedDark",
    "headshot1",
    "headshot2",
] as const;

export type SvgThumbnailColorKey = (typeof COLOR_KEYS)[number];
export type SvgThumbnailImageKey = (typeof IMAGE_KEYS)[number];

export type ColorsObject = {[k in SvgThumbnailColorKey]?: string};
export type ImagesObject = {[k in SvgThumbnailImageKey]?: string};

export const TemplateSvgThumbnail = ({colors={}, images={}, svgCode, React, onError: emitError}:{
    /** Object with color keys as keys, and CSS color values as values. ie `{primary: "#fff"}` */
    colors ?: ColorsObject,
    /** Object with image keys as keys, and image URLs as values. ie `{photo01: "https://meow.com/img.jpg"}` */
    images ?: ImagesObject,
    /** The actual SVG source code. ie `<svg></svg>` */
    svgCode : string,
    /** Callback for errors */
    onError?: () => void,
    /**
     * The React library, because @link https://github.com/facebook/react/issues/13991
     * TODO: package `@Common` to allow npm to dedupe React
     */
    React   : Pick<typeof ReactLib, "useEffect" | "useRef">,
}) =>
{
    const mainRef = React.useRef<SVGSVGElement|null>(null);

    React.useEffect(() =>
    {
        if (!mainRef.current) return;
        const liveSvg = mainRef.current;

        if (!svgCode) return;

        const srcDom = (new DOMParser).parseFromString(svgCode, "image/svg+xml");
        if (srcDom.documentElement?.tagName !== "svg") {
            console.error("Failed to parse SVG", srcDom.activeElement);
            liveSvg.innerHTML = "";
            emitError?.();
            return;
        }

        const srcSvg = srcDom.documentElement!;
        liveSvg.setAttribute("viewBox", srcSvg.getAttribute("viewBox") ?? "");
        liveSvg.innerHTML = srcSvg.innerHTML;

        replaceElements({colors, images, svg: liveSvg});
    }, [mainRef.current, svgCode]);

    React.useEffect(
        () => replaceElements({colors, images, svg: mainRef.current}),
        [JSON.stringify(colors), JSON.stringify(images)]
    );

    return (<svg ref={mainRef}></svg>);
};

const classNameFromKey = (key:string): string => "template-thumb-" + (
    key
    .replace(/[A-Z]/g, (c) => "-" + c.toLowerCase())
    .replace(/[0-9]+/g, (d) => `-${d}`)
);

const replaceElements = function({colors, images, svg}:{
    colors: ColorsObject,
    images: ImagesObject,
    svg: SVGSVGElement,
})
{
    const definedColorEntries = Object.entries(colors).filter(([, val]) => !!val);
    for (const [colorKey, colorValue] of definedColorEntries) {
        const colorClassName = classNameFromKey(`color-${colorKey}`);
        svg.querySelectorAll(`.${colorClassName}`).forEach(
            element => element.setAttribute("fill", colorValue)
        );
    }

    const definedImageEntries = Object.entries(images).filter(([, href]) => !!href);
    for (const [imageKey, imageHref] of definedImageEntries) {
        const imageClassName = classNameFromKey(imageKey);
        svg.querySelectorAll(`.${imageClassName}`).forEach(
            element => element.setAttributeNS("http://www.w3.org/1999/xlink", "href", imageHref)
        );
    }
};
